import React, { useEffect, useState, useRef } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import * as moment from "moment";
import axios from "axios";
import {
  TABLE_QUOTES_LIST,
  QUOTES_LIST_ENDPOINT,
  FILTER_TYPE_MULTI_SELECT_DROPDOWN,
  quoteTypeOptions,
} from "@utils/constants";
import { Title } from "./QuoteList.styled";
import { loadAllQuotes } from "./QuoteList.actionCreators";
import { setAllQuotesLoadingState } from "./QuoteList.slice";
import EncloseTable from "@shared/baseUI/EnhancedTable/EncloseTable";
import { columnHeader, isEmpty } from "@utils/utils";
import UserInfo from "@utils/UserInfo";
import QuoteId from "./components/QuoteId";
import SiteInfo from "./components/SiteInfo";
import QuoteStatus from "./components/QuoteStatus";
import QuoteGeneralInfo from "./components/QuoteGeneralInfo";
import QuoteAction from "./components/QuoteAction";
import QuotePersonInCharge from "./components/QuotePersonInCharge";

const PrepareDataTable = (props) => {
  const {
    t,
    isRefreshList,
    updateRefeshStatus,
    columnList,
    countryId,
    dependancies,
    jobsiteNeedKickoff,
    entityId,
  } = props;

  const prevCountRef = useRef();
  const [data, setData] = useState([]);
  const [status, setStatus] = useState(1);
  const [isRefreshDataAgain, setRefreshState] = useState(false);
  const [skipPageReset, setSkipPageReset] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [requestData, setRequestData] = useState({});
  const [currentPageToFirst, setCurrentPageToFirst] = useState(false);
  const [additionalFilters, setAdditionalFilters] = useState([]);
  const [isAdminSuggestionsLoading, setAdminSuggestionsLoading] =
    useState(false);
  const [adminList, setAdminList] = useState([]);

  const mainCountryInfo = UserInfo.countryInfo();

  const [selectedFilter, setSelectedFilter] = useState([
    {
      column: "country",
      value: {
        value: mainCountryInfo?.country?.id,
        label: t(mainCountryInfo?.country?.name),
      },
    },
  ]);
  const countryList = UserInfo.countryListForFilter(t);

  const dataURL = QUOTES_LIST_ENDPOINT;
  const dispatch = useDispatch();

  let columns = [
    {
      Header: t("QUOTE"),
      title: "QUOTE",
      accessor: "idInfo",
      // eslint-disable-next-line react/prop-types
      Cell: ({ cell: { value } }) => (
        <QuoteId
          data={"#" + value.id}
          id={value.id}
          name={value.name}
          isActive={value.isActive}
          siteId={value.siteId}
          updateRefeshStatus={updateRefeshStatus}
        />
      ),
      disableFilters: true,
    },
    {
      Header: t("SITE_COMPANY"),
      title: "SITE_COMPANY",
      accessor: "siteCompany",
      // eslint-disable-next-line react/prop-types
      Cell: ({ cell: { value } }) => <SiteInfo data={value} />,
      disableFilters: true,
    },
    {
      Header: t("QUOTE_SALESMAN"),
      title: "QUOTE_SALESMAN",
      accessor: "personInCharge",
      // eslint-disable-next-line react/prop-types
      Cell: ({ cell: { value } }) => <QuotePersonInCharge data={value} />,
      disableFilters: true,
    },
    {
      Header: t("QUOTE_STATUS"),
      title: "QUOTE_STATUS",
      accessor: "statusInfo",
      // eslint-disable-next-line react/prop-types
      Cell: ({ cell: { value } }) => <QuoteStatus status={value.status} />,
      disableFilters: true,
    },
    {
      Header: t("QUOTE_TOTAL"),
      title: "QUOTE_TOTAL",
      accessor: "totalExcludingTax",
      // eslint-disable-next-line react/prop-types
      Cell: ({ cell: { value } }) => (
        <QuoteGeneralInfo data={value} column="quoteTotal" />
      ),
      disableFilters: true,
    },
    {
      Header: t("QUOTE_VALIDATION_DATE"),
      title: "QUOTE_VALIDATION_DATE",
      accessor: "validationDate",
      // eslint-disable-next-line react/prop-types
      Cell: ({ cell: { value } }) => (
        <QuoteGeneralInfo data={value} column="validationDate" />
      ),
      disableFilters: true,
    },
    {
      Header: t("QUOTE_DUNNING_INFO"),
      title: "QUOTE_DUNNING_INFO",
      accessor: "dunningInfo",
      // eslint-disable-next-line react/prop-types
      Cell: ({ cell: { value } }) => (
        <QuoteGeneralInfo data={value} column="dunningInfoData" />
      ),
      disableFilters: true,
    },
    {
      Header: t("ACTIONS"),
      title: "ACTIONS",
      accessor: "action",
      // eslint-disable-next-line react/prop-types
      Cell: ({ cell: { value } }) => (
        <QuoteAction
          quoteId={value.id}
          siteId={value.siteId}
          status={value.status}
          onRefreshData={setRefreshState}
          countryId={countryId}
          pageType="list"
          jobsiteNeedKickoff={value.needKickoff}
        />
      ),
      disableFilters: true,
    },
  ];

  columns = columnHeader(columns, columnList);

  const QuoteListByIds = useSelector((state) => state.quoteList.byId);
  const QuoteListAllIds = useSelector((state) => state.quoteList.allIds);
  const filteredRecords = useSelector(
    (state) => state.quoteList.filteredRecords
  );

  const isQuoteListLoading = useSelector((state) => state.quoteList.isLoading);

  useEffect(() => {
    setLoading(isQuoteListLoading);
  }, [isQuoteListLoading]);

  /**
   * Method to fetch set of records based on the filters applied.
   * @param {pageSize} pageSize
   * @param {pageStart} pageStart
   * @param {globalFilter} globalFilter
   * @param {sort} sort
   * @param {appliedFilters} appliedFilters
   */
  const fetchRecords = ({
    pageSize,
    pageStart,
    globalFilter,
    sort,
    appliedFilters,
    currentPage,
  }) => {
    // save request data,
    setRequestData({
      pageSize,
      pageStart,
      globalFilter,
      sort,
      appliedFilters,
      currentPage,
      dependancies,
    });

    let salesman = [];
    let statusFilters = [];
    let selectedCountries = [];
    if (additionalFilters.length > 0) {
      const salesmanFilter = additionalFilters.filter(
        (filterItem) => filterItem.column === "salesman"
      );
      salesman = salesmanFilter?.map((item) => item.value);

      const statusFilter = additionalFilters.filter(
        (filterItem) => filterItem.column === "quoteStatus"
      );
      statusFilters = statusFilter?.map((item) => item.value);

      const countryTypeFilter = additionalFilters.filter(
        (filterItem) => filterItem.column === "country"
      );
      selectedCountries = countryTypeFilter?.map((item) => item.value);
    }

    if (selectedCountries.length === 0) {
      selectedCountries = countryId;
    }

    // Set the loading state
    setLoading(true);
    dispatch(setAllQuotesLoadingState());
    dispatch(
      loadAllQuotes({
        dataURL,
        siteId: entityId ?? "",
        pageSize,
        currentPage,
        sort,
        search: globalFilter,
        status,
        statusFilters,
        salesman,
        dependancies,
        selectedCountries,
      })
    );
  };

  useEffect(() => {
    if (QuoteListAllIds.length > 0) {
      const responseData = QuoteListAllIds.map((id) => {
        const tempData = { ...QuoteListByIds[id] };
        tempData.idInfo = {
          id: tempData.id,
          name: tempData?.name,
          isActive: tempData.status,
          siteId: tempData?.site?.id,
        };
        tempData.siteCompany = {
          siteId: tempData?.site?.id,
          siteName: tempData?.site?.name,
          companyName: tempData?.company?.name,
          isActive: tempData.status,
        };
        tempData.statusInfo = {
          status: tempData.status,
        };
        tempData.totalExcludingTax = {
          currency: tempData.currency,
          totalExcludingTax: tempData?.totalExcludingTax,
          isActive: tempData.status,
          id: tempData.id,
        };
        tempData.validationDate = {
          wonDate: tempData.wonDate,
          estimatedValidationDate: tempData?.estimatedValidationDate,
          isActive: tempData.status,
          status: parseInt(tempData.status, 10),
          id: tempData.id,
        };
        tempData.dunningInfo = {
          dunningDate: tempData.dunningDate,
          followupComment: tempData?.followupComment,
          isActive: tempData.status,
          id: tempData.id,
        };
        tempData.action = {
          id: tempData.id,
          siteId: tempData?.site?.id,
          needKickoff: tempData?.site?.needKickoff,
          status: tempData.status,
        };
        tempData.personInCharge = {
          id: tempData.id,
          sales: tempData?.personInCharge?.sales,
          isActive: tempData.status,
        };

        return tempData;
      });

      setData([...responseData]);
      setPageCount(filteredRecords);
      setLoading(false);
    } else {
      setData([]);
      setPageCount(0);
      setLoading(false);
    }
  }, [t, QuoteListAllIds, QuoteListByIds, filteredRecords]);

  useEffect(() => {
    if (isRefreshDataAgain) {
      setRefreshState(false);
    }
    if (isRefreshList) {
      setRefreshState(true);
    }
  }, [isRefreshDataAgain, isRefreshList]);

  const changeStatus = () => {
    const statusNew = status === 1 ? 2 : 1;
    setStatus(statusNew);
  };

  useEffect(() => {
    // everyTime we apply an custom filter we reset the page to 1
    setCurrentPageToFirst(true);
    if (!isEmpty(requestData) && requestData.currentPage === 1) {
      fetchRecords({
        pageSize: requestData.pageSize,
        pageStart: requestData.pageStart,
        globalFilter: requestData.globalFilter,
        sort: requestData.sort,
        currentPage: requestData.currentPage,
        appliedFilters: additionalFilters,
        dependancies,
      });
    }
  }, [status, additionalFilters]);

  const getAdmins = (countryIdList) => {
    setAdminSuggestionsLoading(true);
    axios
      .get(
        `/general/search?&limit=-1&countries=[${countryIdList}]&objectTypes=["admin"]`
      )
      .then((response) => {
        const entityGroupList = [];
        if (response?.data?.admins) {
          response.data.admins.map((key) => {
            entityGroupList.push({
              label: key.id + " - " + key.firstName + " " + key.lastName,
              value: key.id,
            });
          });
        }
        setAdminList(entityGroupList);
        setAdminSuggestionsLoading(false);
      })
      .catch((error) => {
        if (window.debugMode) {
          Console.error(
            "Error: an error occurred while fetching admins",
            error
          );
        }
        setAdminList([]);
        setAdminSuggestionsLoading(false);
      });
  };

  useEffect(() => {
    getAdmins(countryId);
  }, []);

  useEffect(() => {
      const previousCountry = prevCountRef.current;
      const countryTypeFilter = additionalFilters.filter(
        (filterItem) => filterItem.column === "country"
      );
      let selectedCountries = countryTypeFilter?.map((item) => item.value);
      if (selectedCountries.length === 0) {
        selectedCountries = [countryId];
      }
      prevCountRef.current = selectedCountries;
      const isSame =
        selectedCountries?.length === previousCountry?.length &&
        selectedCountries.every(
          (value, index) => value === previousCountry[index]
        );
        console.log("selectedCountries", selectedCountries);
      if (previousCountry?.length > 0 && !isSame) getAdmins(selectedCountries);
    }, [additionalFilters]);

  return (
    <>
      <Title>{t("QUOTES")}</Title>

      <EncloseTable
        type={TABLE_QUOTES_LIST}
        columns={columns}
        data={data}
        setData={setData}
        initialPageSize={30}
        isRefreshDataAgain={isRefreshDataAgain}
        setSkipPageReset={setSkipPageReset}
        skipPageReset={skipPageReset}
        pageCount={pageCount}
        currentPageToFirst={currentPageToFirst}
        setCurrentPageToFirst={setCurrentPageToFirst}
        setPageCount={setPageCount}
        fetchRecords={fetchRecords}
        loading={loading}
        setAdditionalFilters={setAdditionalFilters}
        additionalFilters={additionalFilters}
        defaultSortOptions={[{ id: "id", desc: true, dir: "desc" }]}
        selectedFilter={selectedFilter}
        extraFilters={[
          ...(countryList?.length > 1
            ? [
                {
                  type: FILTER_TYPE_MULTI_SELECT_DROPDOWN,
                  label: t("COUNTRY"),
                  name: "country",
                  labelClass: "enquiry-listing__admins-list-label",
                  containerClass:
                    "enquiry-listing__status-list-dropdown-container",
                  options: countryList,
                  placeholder: t("SEARCH"),
                  defaultOption: "Nothing Selected",
                },
              ]
            : []),
          {
            type: FILTER_TYPE_MULTI_SELECT_DROPDOWN,
            label: t("ENQUIRIES_FILTER_STEP"),
            placeholder: t("SEARCH"),
            name: "quoteStatus",
            labelClass: "enquiry-listing__admins-list-label",
            containerClass: "enquiry-listing__status-list-dropdown-container",
            defaultOption: "Nothing Selected",
            options: quoteTypeOptions(t),
          },
          {
            type: FILTER_TYPE_MULTI_SELECT_DROPDOWN,
            label: t("ENQUIRIES_FILTER_PERSON_IN_CHARGE"),
            placeholder: t("SEARCH"),
            name: "salesman",
            labelClass: "enquiry-listing__admins-list-label",
            containerClass: "enquiry-listing__status-list-dropdown-container",
            defaultOption: "Nothing Selected",
            options: adminList,
          },
        ]}
      />
    </>
  );
};

PrepareDataTable.propTypes = {
  t: PropTypes.func.isRequired,
  isRefreshList: PropTypes.bool.isRequired,
  columnList: PropTypes.array.isRequired,
  updateRefeshStatus: PropTypes.func,
  dependancies: PropTypes.string,
  entityId: PropTypes.number,
};
PrepareDataTable.defaultProps = {
  updateRefeshStatus: {},
  dependancies: "",
  entityId: "",
};

export default withTranslation()(PrepareDataTable);
